import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Play, Pause, SkipForward, SkipBack, Check, Share2, Heart, RotateCcw } from 'lucide-react';
import { audioApi, getAudioFileUrl } from '@/lib/api';
import { AUDIO_COLORS, LANGUAGE_FLAGS } from '@/lib/constants';
import { useAuth } from '@/contexts/AuthContext';
import { shareAudio } from '@/lib/share';

const HiloPage = () => {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [audios, setAudios] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [listenedSteps, setListenedSteps] = useState([]);
  const audioRef = useRef(null);

  useEffect(() => {
    loadThread();
    // Cargar pasos escuchados del localStorage
    const saved = localStorage.getItem(`hilo_progress_${threadId}`);
    if (saved) setListenedSteps(JSON.parse(saved));
  }, [threadId]);

  const loadThread = async () => {
    try {
      // Cargar el hilo (audio principal + sus respuestas/pasos)
      const data = await audioApi.getThread(threadId);
      setThread(data.thread);
      setAudios(data.audios || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading thread:', error);
      setLoading(false);
    }
  };

  const playStep = (index) => {
    setCurrentIndex(index);
    setIsPlaying(true);
  };

  const markAsListened = (index) => {
    const newListened = [...listenedSteps];
    if (!newListened.includes(index)) {
      newListened.push(index);
      setListenedSteps(newListened);
      localStorage.setItem(`hilo_progress_${threadId}`, JSON.stringify(newListened));
    }
  };

  const playNext = () => {
    if (currentIndex < audios.length - 1) {
      markAsListened(currentIndex);
      setCurrentIndex(currentIndex + 1);
      setIsPlaying(true);
    } else {
      // Fin del hilo
      markAsListened(currentIndex);
      setIsPlaying(false);
    }
  };

  const playPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsPlaying(true);
    }
  };

  const resetProgress = () => {
    setListenedSteps([]);
    localStorage.removeItem(`hilo_progress_${threadId}`);
    setCurrentIndex(0);
  };

  useEffect(() => {
    if (audioRef.current && audios[currentIndex]) {
      audioRef.current.src = getAudioFileUrl(audios[currentIndex].audio_url);
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, currentIndex, audios]);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const total = audioRef.current.duration || 1;
      setProgress((current / total) * 100);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-purple-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!thread || audios.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <button onClick={() => navigate(-1)} className="text-white/70 flex items-center gap-2 mb-6">
          <ArrowLeft size={20} /> Volver
        </button>
        <div className="text-center py-12">
          <p className="text-white/60">Hilo no encontrado</p>
        </div>
      </div>
    );
  }

  const currentAudio = audios[currentIndex];
  const colorData = AUDIO_COLORS[thread.color] || AUDIO_COLORS.blue;
  const progressPercent = Math.round((listenedSteps.length / audios.length) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pb-32">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10 p-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-white/70">
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h1 className="text-white font-bold">{thread.title || 'Hilo de audio'}</h1>
            <p className="text-white/50 text-sm">{audios.length} pasos • {progressPercent}% completado</p>
          </div>
          <button 
            onClick={resetProgress}
            className="text-white/50 p-2 hover:text-white"
            title="Reiniciar progreso"
          >
            <RotateCcw size={20} />
          </button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Barra de progreso general */}
        <div className="bg-white/10 rounded-full h-2 mb-6 overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Lista de pasos */}
        <div className="space-y-2">
          {audios.map((audio, idx) => {
            const isCurrentStep = currentIndex === idx;
            const isListened = listenedSteps.includes(idx);
            const stepColor = AUDIO_COLORS[audio.color] || colorData;
            
            return (
              <div
                key={audio.audio_id}
                onClick={() => playStep(idx)}
                className={`rounded-xl overflow-hidden cursor-pointer transition-all ${
                  isCurrentStep ? 'ring-2 ring-green-400' : ''
                }`}
              >
                <div className={`flex items-center gap-3 p-3 ${
                  isCurrentStep ? 'bg-white/15' : isListened ? 'bg-green-500/10' : 'bg-white/5'
                }`}>
                  {/* Número de paso */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold ${
                    isListened ? 'bg-green-500 text-white' : 'bg-white/20 text-white/70'
                  }`}>
                    {isListened ? <Check size={20} /> : idx + 1}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm">
                      Paso {idx + 1}: {audio.title || stepColor.name}
                    </p>
                    <p className="text-white/50 text-xs">{audio.duration_seconds}s</p>
                  </div>
                  
                  {/* Play/Estado */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCurrentStep && isPlaying ? 'bg-green-500' : 'bg-white/20'
                  }`}>
                    {isCurrentStep && isPlaying ? (
                      <Pause size={18} className="text-white" />
                    ) : (
                      <Play size={18} className="text-white ml-0.5" />
                    )}
                  </div>
                </div>

                {/* Barra de progreso del paso actual */}
                {isCurrentStep && (
                  <div className="h-1 bg-white/10">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>

      {/* Player fijo inferior */}
      {currentAudio && (
        <div className="fixed bottom-0 left-0 right-0 bg-black/95 backdrop-blur-xl border-t border-white/10 p-4">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center gap-3">
              <button onClick={playPrev} disabled={currentIndex === 0} className="text-white/50 disabled:opacity-30">
                <SkipBack size={24} />
              </button>
              
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className={`w-14 h-14 rounded-full flex items-center justify-center ${
                  isPlaying ? 'bg-green-500' : 'bg-purple-500'
                }`}
              >
                {isPlaying ? <Pause size={28} className="text-white" /> : <Play size={28} className="text-white ml-1" />}
              </button>
              
              <button onClick={playNext} disabled={currentIndex >= audios.length - 1} className="text-white/50 disabled:opacity-30">
                <SkipForward size={24} />
              </button>
              
              <div className="flex-1 text-center">
                <p className="text-white text-sm font-medium">Paso {currentIndex + 1} de {audios.length}</p>
                <p className="text-white/50 text-xs">{currentAudio.duration_seconds}s</p>
              </div>
              
              <button
                onClick={() => shareAudio(currentAudio, window.location.origin)}
                className="text-white/50 p-2"
              >
                <Share2 size={20} />
              </button>
            </div>
          </div>
          
          <audio
            ref={audioRef}
            onTimeUpdate={handleTimeUpdate}
            onEnded={playNext}
          />
        </div>
      )}
    </div>
  );
};

export default HiloPage;
